import {
  type BillingAddressForm,
  fetchBillingDetail,
  selectBillingDetail,
  updateBillingAddress,
  validateBillingAddressForm,
} from "@app/billing";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import { useEffect, useState } from "react";
import {
  Banner,
  BannerMessages,
  Box,
  Button,
  FormGroup,
  Group,
  Input,
  Select,
  TitleBar,
  countries,
  statesAndProvinces,
} from "../shared";

function getAddressForm(
  address: Record<string, string> = {},
): BillingAddressForm {
  return {
    streetOne: address.street_one || "",
    streetTwo: address.street_two || "",
    city: address.city || "",
    state: address.state || "NA",
    postCode: address.post_code || "",
    country: address.country || "US",
  };
}

export const BillingAddressPage = () => {
  const org = useSelector(selectOrganizationSelected);
  const billingDetail = useSelector(selectBillingDetail);
  const billingDetailId = org.billingDetailId || org.id;
  const dispatch = useDispatch();
  const loader = useLoader(updateBillingAddress);
  const [error, setError] = useState("");
  const [form, setForm] = useState<BillingAddressForm>(getAddressForm());

  useQuery(fetchBillingDetail({ id: billingDetailId }));
  const billingAddress = billingDetail.organizationDetailsJson
    ?.billing_address as Record<string, string> | undefined;

  useEffect(() => {
    setForm(getAddressForm(billingAddress));
  }, [
    billingAddress?.street_one,
    billingAddress?.street_two,
    billingAddress?.city,
    billingAddress?.state,
    billingAddress?.post_code,
    billingAddress?.country,
  ]);

  useLoaderSuccess(loader, () => {
    setError("");
  });

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    const validationError = validateBillingAddressForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    dispatch(
      updateBillingAddress({
        id: billingDetailId,
        address1: form.streetOne.trim(),
        address2: form.streetTwo.trim(),
        city: form.city.trim(),
        state: form.state,
        zipcode: form.postCode.trim(),
        country: form.country,
      }),
    );
  };

  return (
    <Group>
      <TitleBar description="Update your billing address for invoicing and tax">
        Billing Address
      </TitleBar>
      {error ? <Banner variant="error">{error}</Banner> : null}
      <BannerMessages {...loader} />

      <Box size="md">
        <form className="space-y-4" onSubmit={onSubmit}>
          <FormGroup label="Street Address" htmlFor="street-one">
            <Input
              id="street-one"
              value={form.streetOne}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setForm((prev) => ({
                  ...prev,
                  streetOne: value,
                }));
              }}
              required
            />
          </FormGroup>

          <FormGroup label="Street Address Line 2" htmlFor="street-two">
            <Input
              id="street-two"
              value={form.streetTwo}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setForm((prev) => ({
                  ...prev,
                  streetTwo: value,
                }));
              }}
            />
          </FormGroup>

          <FormGroup label="City" htmlFor="city">
            <Input
              id="city"
              value={form.city}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setForm((prev) => ({ ...prev, city: value }));
              }}
              required
            />
          </FormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="State/Province" htmlFor="state">
              <Select
                id="state"
                ariaLabel="State/Province"
                value={form.state}
                options={statesAndProvinces}
                onSelect={(option) =>
                  setForm((prev) => ({ ...prev, state: option.value }))
                }
              />
            </FormGroup>
            <FormGroup label="Postal/Zip Code" htmlFor="post-code">
              <Input
                id="post-code"
                value={form.postCode}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setForm((prev) => ({
                    ...prev,
                    postCode: value,
                  }));
                }}
                required
              />
            </FormGroup>
          </div>

          <FormGroup label="Country" htmlFor="country">
            <Select
              id="country"
              ariaLabel="Country"
              value={form.country}
              options={countries}
              onSelect={(option) =>
                setForm((prev) => ({ ...prev, country: option.value }))
              }
            />
          </FormGroup>

          <Button type="submit" isLoading={loader.isLoading}>
            Save
          </Button>

          <p className="text-sm text-black-500">
            The information you provide here may affect your sales tax rate in
            accordance with recent developments in state sales tax laws.
          </p>
        </form>
      </Box>
    </Group>
  );
};
